"use client";

import { Badge, Button, ButtonSetSlide, Icon, TIconKey } from "@cloai/uikit";
import { isEmpty, map } from "lodash";
import { useTranslations } from "next-intl";
import { FC, memo, useMemo } from "react";

import { useABtestContext, useUndressSettingsContext } from "@/providers";
import { cn } from "@/utils/cn";

/**
 * Свойства для компонента `Tab`
 */
type TTabProps = {
  index: number;
  icon: TIconKey;
  text: string;
  onClick: (index: number) => () => void;
  isNew?: boolean;
};

/**
 * Компонент вкладки (таба).
 * @param index - Индекс вкладки
 * @param icon - Иконка вкладки
 * @param text - Текст вкладки
 * @param onClick - Функция обработки клика по вкладке
 * @param new - Маркер для баджа "NEW"
 */
const Tab: FC<TTabProps> = memo(({ index, icon, text, onClick, isNew }) => (
  <Badge
    content="NEW"
    color="secondary"
    size="lg"
    variant="shadow"
    wide
    isInvisible={!isNew}
  >
    <Button
      startContent={<Icon icon={icon} size={5} />}
      variant="light"
      size="md"
      wide
      centerContent
      className="rounded-xl"
      onClick={onClick(index)}
    >
      {text}
    </Button>
  </Badge>
));
Tab.displayName = "Tab";

type TTabsList = {
  icon: TIconKey;
  text: string;
}[];

/**
 * Компонент вкладок для управления настройками Undress.
 */
const Tabs: FC = () => {
  const t = useTranslations("HomePage.settingsPopup");
  const { activeTabIndex, handleTabClick, sets, storylines } =
    useUndressSettingsContext();
  const { AAB_RedesignV1 } = useABtestContext();

  /**
   * Массив вкладок
   */
  const tabList: TTabsList = useMemo(
    () =>
      [
        !isEmpty(storylines) && {
          icon: "cards_star",
          text: t("settingsStorylines"),
        },
        !isEmpty(sets) && { icon: "filter", text: t("settingsSets") },
      ].filter(Boolean) as never,

    [sets, storylines, t],
  );

  if (isEmpty(tabList)) {
    return null;
  }

  return (
    <div className={cn("w-full", AAB_RedesignV1 && "mx-auto max-w-110")}>
      <ButtonSetSlide activeButton={activeTabIndex} wide>
        {map(tabList, (tab, index) => (
          <Tab
            key={index}
            index={index}
            icon={tab.icon}
            text={tab.text}
            onClick={handleTabClick}
          />
        ))}
      </ButtonSetSlide>
    </div>
  );
};

export default memo(Tabs);
